import styles from 'components/compliance/risk/risks.module.css';
import { AI_RISK_SYMBOL } from 'lib/constants/risks';
import { DEFAULT_PROFILE_COLORS } from 'lib/constants/users';
import { v4 as uuidv4, validate as validateUUID } from 'uuid';
import { utils, writeFile } from 'xlsx';

const BROWSER_ID_KEY = 'linesight-browser-id';

export const getOrgNameById = (orgId, orgList) => {
  const org = orgList.find((org) => org._id === orgId);
  return org ? org.name : 'Unknown';
};

export const getRiskMqttTopic = (sessionId) => {
  return `linesight/risks/${sessionId}`;
};

export const RISK_LEVEL_NUM = styles['risk-level-num'];

export const getRiskScoreLevel = (score) => {
  if (!score) return 'none';
  if (score >= 36) return 'high';
  if (score >= 24) return 'med';
  if (score >= 1) return 'low';
  return 'none';
};

export const getRiskScoreClass = (score) => {
  if (Number(score) >= 1 && Number(score) <= 23) {
    return styles['risk-level-low'];
  } else if (Number(score) >= 24 && Number(score) <= 35) {
    return styles['risk-level-med'];
  } else if (Number(score) >= 36) {
    // && Number(score) <= 125
    return styles['risk-level-high'];
  }
  return styles['risk-level-none'];
};

export const getRiskNumericClass = (prob) => {
  if (Number(prob) >= 1 && Number(prob) <= 6) return styles[`risk-level-${Number(prob)}`];
  if (Number(prob) > 6) return styles['risk-level-high'];
  return styles['risk-level-none'];
};

export const getBrowserId = () => {
  if (
    !!localStorage.getItem(BROWSER_ID_KEY) &&
    validateUUID(localStorage.getItem(BROWSER_ID_KEY))
  ) {
    return localStorage.getItem(BROWSER_ID_KEY);
  } else {
    const newId = uuidv4();
    localStorage.setItem(BROWSER_ID_KEY, newId);
    return newId;
  }
};

export const haNumComparator = (a, b) => {
  const aNum = Number(a.replace(/[^\d.]/g, ''));
  const bNum = Number(b.replace(/[^\d.]/g, ''));
  return aNum - bNum;
};

export const mapRiskValuesWithAI = (risk) => {
  risk._riskScores = {
    detectability:
      risk.riskScores?.detectability > 0
        ? risk.riskScores?.detectability
        : risk.aiScores?.detectability,
    probability:
      risk.riskScores?.probability > 0 ? risk.riskScores?.probability : risk.aiScores?.probability,
    severity: risk.riskScores?.severity > 0 ? risk.riskScores?.severity : risk.aiScores?.severity,
    riskScore:
      risk.riskScores?.riskScore > 0 ? risk.riskScores?.riskScore : risk.aiScores?.riskScore,
    detectabilityFm:
      risk.riskScores?.detectabilityFm > 0
        ? risk.riskScores?.detectabilityFm
        : risk.aiScores?.detectabilityFm,
    probabilityFm:
      risk.riskScores?.probabilityFm > 0
        ? risk.riskScores?.probabilityFm
        : risk.aiScores?.probabilityFm,
    severityFm:
      risk.riskScores?.severityFm > 0 ? risk.riskScores?.severityFm : risk.aiScores?.severityFm,
    riskScoreFm:
      risk.riskScores?.riskScoreFm > 0 ? risk.riskScores?.riskScoreFm : risk.aiScores?.riskScoreFm
  };
  risk._mitigationStrategy = risk.mitigationStrategy
    ? risk.mitigationStrategy
    : risk.mitigationStrategyAI
    ? `${AI_RISK_SYMBOL} ${risk.mitigationStrategyAI}`
    : '';
  return risk;
};

export const exportToExcel = (rows = [], columns = [], orgName = '', productName = '') => {
  try {
    const exportColumns = columns.filter((col) => col.field !== 'actions');
    const headers = exportColumns.map((col) => col.headerName);
    const data = rows.map((row) => {
      return exportColumns.map((col) => {
        if (col.valueGetter) {
          try {
            return col.valueGetter(null, row);
          } catch (error) {
            console.error('Error getting value:', error);
            return '';
          }
        }
        return row[col.field] ?? ''; // Use nullish coalescing to handle null/undefined
      });
    });

    const excelData = [headers, ...data];
    const ws = utils.aoa_to_sheet(excelData);

    // Set column widths with error handling
    const colWidths = exportColumns.map((col) => ({
      wch: Math.max(
        col.headerName?.length || 0,
        ...data.map((row) => String(row[exportColumns.indexOf(col)] || '').length)
      )
    }));
    ws['!cols'] = colWidths;

    // Create workbook and append worksheet
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Risk Assessment');

    // Generate filename with sanitized org name and product name
    const date = new Date().toISOString().split('T')[0];
    const sanitizedOrgName = (orgName || 'unknown-org')
      .trim()
      .replace(/[^a-zA-Z0-9]/g, '-')
      .replace(/-+/g, '-'); // Replace multiple consecutive hyphens with single hyphen
    const sanitizedProductName = (productName || 'unknown-product')
      .trim()
      .replace(/[^a-zA-Z0-9]/g, '-')
      .replace(/-+/g, '-');

    const fileName = `${sanitizedOrgName}_${sanitizedProductName}_risk-assessment_${date}.xlsx`;

    writeFile(wb, fileName);
    return true;
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw new Error('Failed to export data to Excel');
  }
};

/* 
  @param queryOpts: an object of query parameters, values should be primatives, no objects or arrays
  @return a query string from an object of query parameters in the ?key=value&key2=value2 format
*/
export const buildQueryString = (queryOpts = {}) => {
  let queryStr = '';
  if (Object.keys(queryOpts).length > 0) {
    queryStr = '?';
    for (const [key, value] of Object.entries(queryOpts)) {
      queryStr += `${key}=${value}&`;
    }
    queryStr = queryStr.slice(0, -1);
  }
  return queryStr;
};

export const stringToColor = (string) => {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
};

// check if user has a color,
// else generate one based on their name,
// else use a random default color
export const getUserColor = (user) => {
  return user?.color
    ? user.color
    : user?.firstName && user?.lastName
    ? stringToColor(`${user.firstName} ${user.lastName}`)
    : DEFAULT_PROFILE_COLORS[Math.floor(Math.random() * DEFAULT_PROFILE_COLORS.length)];
};

export const calculatePercentage = (value, total) => {
  if (!total || total === 0) return 0;
  return Math.round((value / total) * 100);
};

export const calculatePercentages = (compliant, nonCompliant, notStarted, total) => {
  if (!total || total === 0) return [0, 0, 0];

  // Calculate exact percentages
  const compliantExact = (compliant / total) * 100;
  const nonCompliantExact = (nonCompliant / total) * 100;
  const notStartedExact = (notStarted / total) * 100;

  // Round down initially
  let compliantRounded = Math.floor(compliantExact);
  let nonCompliantRounded = Math.floor(nonCompliantExact);
  let notStartedRounded = Math.floor(notStartedExact);

  // Calculate the sum of rounded values
  let sum = compliantRounded + nonCompliantRounded + notStartedRounded;

  // Calculate remaining percentage to distribute (should be 0-2%)
  const remaining = 100 - sum;

  // Distribute the remaining percentage based on fractional parts
  const fractions = [
    { index: 0, value: compliantExact - compliantRounded },
    { index: 1, value: nonCompliantExact - nonCompliantRounded },
    { index: 2, value: notStartedExact - notStartedRounded }
  ].sort((a, b) => b.value - a.value);

  // Distribute remaining percentage points to the values with largest fractional parts
  for (let i = 0; i < remaining; i++) {
    if (i < fractions.length) {
      // Safety check
      if (fractions[i].index === 0) compliantRounded++;
      else if (fractions[i].index === 1) nonCompliantRounded++;
      else if (fractions[i].index === 2) notStartedRounded++;
    }
  }

  return [compliantRounded, nonCompliantRounded, notStartedRounded];
};

export const formatRiskHistoryVotingReviewedAt = (input) => {
  if (!input) return '—';

  const date = new Date(input);
  if (isNaN(date)) return '—';

  const pad = (num) => num.toString().padStart(2, '0');

  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const yy = date.getFullYear().toString().slice(-2);
  const HH = pad(date.getHours());
  const MM = pad(date.getMinutes());
  const SS = pad(date.getSeconds());

  return `${mm}/${dd}/${yy} ${HH}:${MM}:${SS}`;
};

export const formatDateTime = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toISOString().slice(0, 16).replace('T', ' ');
};
